import React from 'react'

import { SimpleTable, TableSkeleton, type AddressCell, Alert } from '@/components/ui'
import { useApi, useDashboard, useSkeletonOnConfigChange } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import { validateDateRangeWithin30Days } from '@/insights/common/formatUtils'
import genericLocationDrilldown from '@/insights/common/genericLocationDrilldown'
import { buildQuery } from '@/insights/common/queryUtils'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import { DateOptions } from '@/insights/components/CustomSingleSelect'
import {
  ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  type InsightParametersType,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'
import { celsiusToUser } from '@/utils/conversions'
import { downloadCsv } from '@/utils/csvExport'
import { formatDate, standardDateTimeFormat } from '@/utils/formatDate'

interface MonitoringPointParams extends InsightParametersType {
  companyId?: number
  assetId?: string
  date?: string
  from?: string
  to?: string
}

interface MonitoringPointReading {
  name: string
  value: number | null
}

interface DoorReading {
  name: string
  status: 'open' | 'closed'
}

interface MonitoringPointItem {
  dateTime: string
  location: {
    address: string | null
    latitude: number | null
    longitude: number | null
  }
  monitoringPoints: MonitoringPointReading[]
  doors: DoorReading[]
}

const MonitoringPointTemperature: InsightDefinition<MonitoringPointParams, MonitoringPointItem[]> = {
  active: true,
  slug: 'asset/monitoring-point',
  category: 'asset',
  name: 'Monitoring Point Temperature',
  description: 'List monitoring point temperatures and door statuses for an asset over time',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    transform: ({ parameters, field }) => {
      if (field === 'date' && parameters.date !== 'custom') {
        return { ...parameters, from: undefined, to: undefined }
      }
      return parameters
    },

    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions.map((d) => (d.value === 'today' ? { ...d, isDefault: true } : d)),
      },
      {
        id: 'customDate',
        label: 'Date',
        control: 'DateTimeRange',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
    ],

    isValid: (configuration: MonitoringPointParams) => {
      if (!configuration.companyId || !configuration.assetId) {
        return 'Company and Asset are required.'
      }

      if (configuration.date === 'custom') {
        if (!configuration.from || !configuration.to) {
          return 'Enter the custom date range'
        }

        const fromdate = new Date(configuration.from)
        const todate = new Date(configuration.to)
        if (fromdate > todate) {
          return 'Start date must be before end date'
        }

        const dateValidation30Days = validateDateRangeWithin30Days(fromdate.toISOString(), todate.toISOString())

        if (dateValidation30Days !== true) {
          return dateValidation30Days
        }
      }

      return true
    },
  },

  load: async (api: ApiService, parameters: MonitoringPointParams) => {
    const path = `/insights/asset/monitoring-point?${buildQuery(parameters)}&skip=0&take=1000`
    const response = await api.get<MonitoringPointItem[]>({
      path,
      origin: 'MonitoringPointTemperature',
    })
    if (response.error) {
      return { code: InsightStatusCode.ERROR, message: response.message }
    }
    if (!response.data?.length) {
      return { code: InsightStatusCode.NO_DATA }
    }
    return response.data
  },

  csv: async (api: ApiService, parameters: MonitoringPointParams, title: string) => {
    const path = '/insights/asset/monitoring-point'
    await downloadCsv(api, path, parameters, title)
  },

  Display: ({ status, data, configuration }) => {
    const showSkeleton = useSkeletonOnConfigChange({ status, data, configuration })
    const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (showSkeleton) {
      return <TableSkeleton columns={6} withHeader rowHeight={35} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING && status.code !== InsightStatusCode.LOADING) {
      return null
    }
    if (isInsightStatus(data)) {
      return null
    }
    if (!data?.length) {
      return null
    }

    const parameters = configuration.parameters as MonitoringPointParams

    // Column names come from the API response. Collect the union of monitoring
    // point names and door names across all samples, preserving first-appearance
    // order so the table stays stable across rows with differing readings.
    const tempNames: string[] = []
    const doorNames: string[] = []
    data.forEach((sample) => {
      sample.monitoringPoints.forEach((mp) => {
        if (!tempNames.includes(mp.name)) {
          tempNames.push(mp.name)
        }
      })
      sample.doors.forEach((door) => {
        if (!doorNames.includes(door.name)) {
          doorNames.push(door.name)
        }
      })
    })

    const headers = [
      'Date',
      'Location',
      ...tempNames.map((name) => ({ key: name, title: `${name}` })),
      ...doorNames.map((name) => ({ key: name, title: `${name}` })),
    ]

    const body = data
      .slice()
      .sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())
      .map((sample) => {
        const row: Record<string, string | number | AddressCell> = {
          Date: formatDate(new Date(sample.dateTime), standardDateTimeFormat),
          Location: genericLocationDrilldown({
            configuration,
            address: sample.location.address ?? undefined,
            latitude: sample.location.latitude ?? undefined,
            longitude: sample.location.longitude ?? undefined,
            companyId: parameters.companyId,
            markerFields: {
              Time: formatDate(new Date(sample.dateTime), standardDateTimeFormat),
            },
          }),
        }

        tempNames.forEach((name) => {
          const mp = sample.monitoringPoints.find((m) => m.name === name)
          row[name] = mp ? celsiusToUser(mp.value, 1, units) : ''
        })

        doorNames.forEach((name) => {
          const door = sample.doors.find((d) => d.name === name)
          row[name] = door ? door.status : ''
        })

        return row
      })

    return (
      <section className="text-base-content text-base h-full flex flex-col">
        <div className="min-h-0 flex-1">
          <SimpleTable headers={headers} body={body} cellClassNames={{ Date: 'text-nowrap' }} />
        </div>
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as MonitoringPointParams
    React.useEffect(() => {
      if (!api) {
        return
      }
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'MonitoringPointTemperature#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) {
        fields['Company'] = company.name
      }
    }
    if (asset) {
      fields['Asset'] = asset.name
    }
    addDateToCaptionFields({ ...params, fields, finite: true })

    if (Object.keys(fields).length) {
      return <Caption fields={fields} />
    }
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || (status.code === InsightStatusCode.ERROR && data === undefined)) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA) {
      return <div>No records found</div>
    }
    const rows = data?.length
    if (!rows) {
      return <div>No records found</div>
    }
    return (
      <div>
        {rows} row{rows === 1 ? '' : 's'} found
      </div>
    )
  },
}

export default MonitoringPointTemperature
